import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Tabs as TabsNavigation, Flex } from 'antd';

import { useEvent } from '../features/events/hooks/useEvent';
import AdminTemplate from '../layout/AdminTemplate';
import requireAuth from '../components/hocs/requireAuth';
import { CacheManagerTab, UrlParametersTab, IgnoredURLsTab } from '../features/cache-manager';
import DocsLink from '../components/DocsLink';

const enum Tabs {
  CacheManager = 'cache-manager',
  UrlParameters = 'caching-rules',
  IgnoredURLs = 'ignored-urls',
}

const LEGACY_URL_PARAMETERS_PATH = 'url-parameters';

const KEY_LABEL_MAP = {
  [Tabs.CacheManager]: 'Cache Manager',
  [Tabs.UrlParameters]: 'Caching Rules',
  [Tabs.IgnoredURLs]: 'Ignored URLs',
};

const TABS = [
  { key: Tabs.CacheManager, label: KEY_LABEL_MAP[Tabs.CacheManager], component: CacheManagerTab },
  { key: Tabs.UrlParameters, label: KEY_LABEL_MAP[Tabs.UrlParameters], component: UrlParametersTab },
  { key: Tabs.IgnoredURLs, label: KEY_LABEL_MAP[Tabs.IgnoredURLs], component: IgnoredURLsTab },
];

const META_TAGS = {
  title: 'Cache Manager - Prerender.io',
};

function NewCacheManagerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useEvent();

  const activeTab = React.useMemo(() => location.pathname.split('/').pop(), [location.pathname]);

  const onTabClick = (activeKey: string) => {
    track(`${KEY_LABEL_MAP[activeKey as Tabs]} tab viewed`);
    // Absolute: relative targets resolve against the full splat match in react-router 7.
    navigate(`/cache/${activeKey}`);
  };

  return (
    <AdminTemplate metaTags={META_TAGS}>
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={2}>Cache Manager</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 4 }}>
          View cached pages, re-cache or remove them, and adjust cache expiration.
        </Typography.Paragraph>
        <Flex gap={8}>
          <DocsLink href="https://docs.prerender.io/docs/cache-expiration">About cache expiration</DocsLink>
          <DocsLink href="https://docs.prerender.io/docs/6-api">API Endpoints</DocsLink>
        </Flex>
      </div>
      <TabsNavigation activeKey={activeTab} items={TABS} onTabClick={onTabClick} />

      <Routes>
        {TABS.map(({ key, component: Component }) => (
          <Route path={key} key={key} element={<Component />} />
        ))}
        <Route
          path={LEGACY_URL_PARAMETERS_PATH}
          element={<Navigate to={`/cache/${Tabs.UrlParameters}`} replace />}
        />
        {/* Absolute target: a relative `to` inside a splat-mounted route resolves against the
            full splat match in react-router 7, so an unknown path would redirect onto itself
            forever (/cache/x -> /cache/x/cache-manager -> ...). */}
        <Route path="*" element={<Navigate to={`/cache/${TABS[0].key}`} replace />}></Route>
      </Routes>
    </AdminTemplate>
  );
}

export default requireAuth(NewCacheManagerPage);
